import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Popover, styled } from '@mui/material';
import { MouseEvent, useCallback, useEffect, useState } from 'react';

import {
  BaseDropdown,
  baseDropdownClasses,
  BaseDropdownTrigger,
  DropdownOffset,
  DropdownTypography,
  EDropdownPlacement,
} from '#entities';

import { useIsIdle } from '../../data-context';
import { useNavigationMenu } from '../model';

export interface PageDropdownProps {
  id?: string;
  value: string;
  placement?: EDropdownPlacement;
  offset?: DropdownOffset;
  onOpenChange?: (open: boolean) => void;
}

const Delimiter = styled(Box)(({ theme }) => ({
  height: '1px',
  margin: `${theme.spacing(1)} ${theme.spacing(1)}`,
  backgroundColor: `${theme.palette.grey[300]}80`,
}));

const ItemRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  height: theme.spacing(4),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(1),
  cursor: active ? 'default' : 'pointer',
  color: theme.palette.common.white,
  backgroundColor: active ? theme.palette.mapBlue[800] : 'transparent',
  pointerEvents: active ? 'none' : 'auto',
  '&:hover': {
    backgroundColor: theme.palette.mapBlue[800],
  },
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& > svg': {
    width: '100%',
    fill: 'currentColor',
    filter: 'grayscale(1) brightness(0) invert(1)',
  },
}));

export const StyledDropdownIcon = styled(MoreHorizIcon)(({ theme }) => ({
  color: theme.palette.common.white,
  fill: theme.palette.common.white,
  display: 'block',
  width: theme.spacing(3),
  height: theme.spacing(3),

  [theme.breakpoints.down('xl')]: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
  },
}));

export const NavigationMenu = ({
  id = 'mainMenu',
  value,
  placement = EDropdownPlacement.Inline,
  offset,
  onOpenChange,
}: PageDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const {
    menu: items,
    handleSwitch: onChange,
    ToastComponent,
  } = useNavigationMenu();
  const isIdle = useIsIdle();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      onOpenChange?.(true);
    },
    [onOpenChange],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    if (isIdle) {
      setTimeout(handleClose, 0);
    }
  }, [handleClose, isIdle]);

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <BaseDropdownTrigger
        placement={placement}
        offset={offset}
        className={baseDropdownClasses.button}
        onClick={handleClick}
      >
        <StyledDropdownIcon className={baseDropdownClasses.icon} />
      </BaseDropdownTrigger>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={0}
        slotProps={{
          paper: {
            sx: {
              overflow: 'visible',
              boxShadow: 'none',
              backgroundColor: 'transparent',
            },
          },
        }}
      >
        <BaseDropdown className={baseDropdownClasses.root}>
          {items.map((item, index) =>
            item.type === 'delimiter' ? (
              <Delimiter key={`delimiter-${index}`} />
            ) : (
              <ItemRow
                key={`${id}-${item.id}`}
                active={value === item.id}
                onClick={() => {
                  if (value !== item.id) {
                    onChange(item.id);
                  }
                  handleClose();
                }}
              >
                <IconBox>{item.icon}</IconBox>
                <DropdownTypography>{item.title}</DropdownTypography>
              </ItemRow>
            ),
          )}
        </BaseDropdown>
      </Popover>
      <ToastComponent />
    </>
  );
};
